import type {
  CreateHeliosDataTableRecordReq,
  CreateHeliosDataTableRecordRes,
  CreateHeliosRecordReq,
  CreateHeliosRecordRes,
  DeleteHeliosRecordReq,
  ExportDataFromTableToFile,
  GetHeliosBrowseItemsReq,
  GetHeliosBrowseItemsRes,
  GetHeliosCalendarsAppointments,
  GetHeliosCalendarsAppointmentsReq,
  GetHeliosDataRecordPrefilledReq,
  GetHeliosDataRecordReq,
  GetNeighborRecordNumbersReq,
  GetSingleHeliosRecord,
  HeliosDataTableItems,
  HeliosRecordDataTablesReq,
  HeliosRecordDataTablesRes,
  NeighborRecordNumbersRes,
  UpdateHeliosTableRecordsReq,
} from 'types/dataHelios';

import { TAG_TYPES } from 'types/services';
import { heliosApi } from '.';

export const heliosDataHeliosService = heliosApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getHeliosDataRecords: builder.query<GetSingleHeliosRecord, GetHeliosDataRecordReq>({
      query: ({ templateId, recordNumber, overviewTemplateId }) => ({
        url: 'DataHelios/DataRecords',
        params: {
          templateId,
          recordNumber,
          ...(overviewTemplateId === undefined ? {} : { overviewTemplateId }),
        } satisfies GetHeliosDataRecordReq,
      }),
      transformErrorResponse: (response, meta) => {
        if (meta?.response?.status === 404) throw new Error('Record not found');
        return response;
      },
      providesTags: [TAG_TYPES.DATA_HELIOS],
    }),
    getHeliosDataRecordsFiltered: builder.query<GetSingleHeliosRecord, GetHeliosDataRecordReq>({
      query: ({ templateId, recordNumber, overviewTemplateId, joinColumnFilters }) => ({
        url: 'DataHelios/DataRecords/Filtered',
        method: 'POST',
        body: {
          templateId,
          recordNumber: Number(recordNumber),
          ...(overviewTemplateId === undefined ? {} : { overviewTemplateId }),
          ...(joinColumnFilters ? { joinColumnFilters } : {}),
        },
      }),
      transformErrorResponse: (response, meta) => {
        if (meta?.response?.status === 404) throw new Error('Record not found');
        return response;
      },
      providesTags: [TAG_TYPES.DATA_HELIOS],
    }),
    getHeliosDataRecordsPrefilled: builder.query<GetSingleHeliosRecord, GetHeliosDataRecordPrefilledReq>({
      query: ({ templateId, recordNumber, parentTemplateId, parentRecordNumber }) => ({
        url: 'DataHelios/DataRecords/Prefilled',
        params: {
          templateId,
          ...(recordNumber === undefined ? {} : { recordNumber }),
          ...(parentTemplateId ? { parentTemplateId } : {}),
          ...(parentRecordNumber ? { parentRecordNumber } : {}),
        },
      }),
    }),
    getHeliosCalendarsAppointments: builder.query<GetHeliosCalendarsAppointments, GetHeliosCalendarsAppointmentsReq>({
      query: ({ TemplateId, DateFrom, DateTo, ColorCategories, CategoryFilter }) => ({
        url: 'DataHelios/Calendars/Appointments',
        params: {
          TemplateId,
          DateFrom,
          DateTo,
          ...(ColorCategories?.length ? { ColorCategories } : {}),
          ...(CategoryFilter?.length ? { CategoryFilter } : {}),
        },
      }),
      providesTags: [TAG_TYPES.DATA_HELIOS],
    }),
    getHeliosPreviewDataTablesTemplateId: builder.query<HeliosDataTableItems, unknown>({
      query: ({ templateId }) => ({
        url: `DataHelios/PreviewDataTables/${templateId}`,
      }),
    }),
    createHeliosDataTablesExport: builder.mutation<string, ExportDataFromTableToFile>({
      query: (data) => ({
        url: 'DataHelios/DataTables/Export',
        method: 'POST',
        body: data,
        // We need to convert the to a base64 string to avoid putting non-serializable values into the Redux store state or dispatched actions
        responseHandler: async (response) => {
          const blob = await response.blob();

          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
              const result = reader.result;
              if (typeof result !== 'string') {
                reject(new Error('Failed to read export as a base64 data URL'));
                return;
              }

              const base64 = result.split(',')[1];

              if (!base64) {
                reject(new Error('Failed to parse base64 payload from data URL'));
                return;
              }

              resolve(base64);
            };

            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        },
      }),
    }),
    getHeliosDataTables: builder.query<HeliosRecordDataTablesRes, HeliosRecordDataTablesReq>({
      query: (data) => ({
        url: `DataHelios/DataTables`,
        method: 'POST',
        body: data,
      }),
      providesTags: [TAG_TYPES.DATA_TABLES_HELIOS],
    }),
    deleteHeliosDataRecord: builder.mutation<unknown, DeleteHeliosRecordReq>({
      query: (data) => ({
        url: 'DataHelios',
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.DATA_HELIOS }],
    }),
    createHeliosDataRecord: builder.mutation<CreateHeliosRecordRes, CreateHeliosRecordReq>({
      query: (data) => ({
        url: 'DataHelios',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.DATA_HELIOS }],
    }),
    createHeliosDataTableRecord: builder.mutation<CreateHeliosDataTableRecordRes, CreateHeliosDataTableRecordReq>({
      query: (data) => ({
        url: 'DataHelios/DataTables/Record',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.DATA_HELIOS }],
    }),
    presaveHeliosDataTableRecord: builder.mutation<CreateHeliosDataTableRecordRes, CreateHeliosDataTableRecordReq>({
      query: (data) => ({
        url: 'DataHelios/DataTables/Record/Presave',
        method: 'POST',
        body: data,
      }),
      // Don't automatically invalidate - we'll handle this conditionally in the component
    }),
    updateHeliosDataTableRecord: builder.mutation<CreateHeliosRecordRes, UpdateHeliosTableRecordsReq & { skipInvalidation?: boolean }>({
      query: ({ skipInvalidation, ...data }) => ({
        url: 'DataHelios/DataTables/Record',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg.skipInvalidation ? [] : [{ type: TAG_TYPES.DATA_HELIOS }, { type: TAG_TYPES.FILES }]),
    }),
    getHeliosBrowseItems: builder.query<GetHeliosBrowseItemsRes, GetHeliosBrowseItemsReq>({
      query: ({ templateId, templateItemId, recordId, entryId }) => ({
        url: 'DataHelios/BrowseItems',
        params: {
          TemplateId: templateId,
          TemplateItemId: templateItemId,
          RecordId: recordId,
          EntryId: entryId,
        },
      }),
      providesTags: [TAG_TYPES.DATA_HELIOS],
    }),
    createHeliosDataTablesRecord: builder.mutation<CreateHeliosDataTableRecordRes, CreateHeliosDataTableRecordReq>({
      query: (data) => ({
        url: 'DataHelios/DataTables/Record',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.DATA_HELIOS }, { type: TAG_TYPES.FILES }],
    }),
    updateHeliosDataTablesRecord: builder.mutation<CreateHeliosDataTableRecordRes, CreateHeliosDataTableRecordReq>({
      query: (data) => ({
        url: 'DataHelios/DataTables/Record',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.DATA_HELIOS }],
    }),
    createHeliosDataTablesRecordSilent: builder.mutation<CreateHeliosDataTableRecordRes, CreateHeliosDataTableRecordReq>({
      query: (data) => ({
        url: 'DataHelios/DataTables/Record',
        method: 'POST',
        body: data,
      }),
    }),
    updateHeliosDataTablesRecordSilent: builder.mutation<
      CreateHeliosDataTableRecordRes,
      CreateHeliosDataTableRecordReq & { _skipInvalidation?: boolean }
    >({
      query: ({ _skipInvalidation, ...data }) => ({
        url: 'DataHelios/DataTables/Record',
        method: 'PATCH',
        body: data,
      }),
    }),
    getNeighborRecordNumbers: builder.query<NeighborRecordNumbersRes, GetNeighborRecordNumbersReq>({
      query: ({ templateId, currentRecordNumber }) => ({
        url: `DataHelios/DataTables/Neighbors?templateId=${templateId}&currentRecordNumber=${currentRecordNumber}`,
      }),
    }),
  }),
});
export const {
  useGetHeliosDataRecordsQuery,
  useGetHeliosDataRecordsFilteredQuery,
  useGetHeliosCalendarsAppointmentsQuery,
  useGetHeliosDataRecordsPrefilledQuery,
  useLazyGetHeliosDataRecordsPrefilledQuery,
  useGetHeliosPreviewDataTablesTemplateIdQuery,
  useCreateHeliosDataTablesExportMutation,
  useDeleteHeliosDataRecordMutation,
  useCreateHeliosDataRecordMutation,
  usePresaveHeliosDataTableRecordMutation,
  useUpdateHeliosDataTableRecordMutation,
  useGetHeliosDataTablesQuery,
  useLazyGetHeliosDataTablesQuery,
  useGetHeliosBrowseItemsQuery,
  useCreateHeliosDataTablesRecordMutation,
  useUpdateHeliosDataTablesRecordMutation,
  useCreateHeliosDataTablesRecordSilentMutation,
  useUpdateHeliosDataTablesRecordSilentMutation,
  useGetNeighborRecordNumbersQuery,
} = heliosDataHeliosService;
